import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiNote } from '../api';
import { updateAlertSuccess } from '../alerts/alertReducer';
import { InternalApiDocument, InternalApiMonitoringAlert, InternalApiPerson } from '../api-internal';

export interface Page<T> {
  content: T[];
  page: number;
  size: number;
  total: number;
}

const initialPageState = <T>(): Page<T> => {
  return {
    content: [],
    page: 0,
    size: 20,
    total: 0,
  };
};

export interface AmlState {
  person: InternalApiPerson | null;
  notes: Page<ApiNote>;
  documents: Page<InternalApiDocument>;
  alerts: Page<InternalApiMonitoringAlert>;
}

export const initialState: AmlState = {
  person: null,
  notes: initialPageState(),
  documents: initialPageState(),
  alerts: initialPageState(),
};

const fetchSuccess = (name: 'notes' | 'documents' | 'alerts') => {
  return (state: AmlState, action: PayloadAction<any>) => {
    state[name].content = action.payload.content;
    state[name].total = action.payload.total;
    state[name].page = action.payload.number;
    state[name].size = action.payload.size;
  };
};

const setPage = (name: 'notes' | 'documents' | 'alerts') => {
  return (state: AmlState, action: PayloadAction<any>) => {
    state[name].page = action.payload;
  };
};

const amlSlice = createSlice({
  name: 'aml',
  initialState,
  reducers: {
    fetchPersonSuccess: (state, action) => {
      return { ...state, person: action.payload };
    },
    clearPersonState: () => {
      return initialState;
    },
    fetchDocumentsSuccess: fetchSuccess('documents'),
    fetchNotesSuccess: fetchSuccess('notes'),
    fetchAlertsSuccess: fetchSuccess('alerts'),
    setDocumentsPage: setPage('documents'),
    setNotesPage: setPage('notes'),
    setAlertsPage: setPage('alerts'),
  },
  extraReducers: (builder) => {
    // After hopelessly trying, I couldn't figure out the correct type for `action` here
    // so shamefully leaving `any` in hopes that eventually this can be picked up and updated
    builder.addCase(updateAlertSuccess.type, (state, action: any) => {
      state.alerts.content = state.alerts.content.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
    });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = amlSlice;
// Extract and export each action creator by name
export const {
  fetchPersonSuccess,
  clearPersonState,
  fetchDocumentsSuccess,
  fetchNotesSuccess,
  fetchAlertsSuccess,
  setDocumentsPage,
  setNotesPage,
  setAlertsPage,
} = actions;
// Export the reducer, either as a default or named export
export default reducer;
